import { useEffect } from "react";

const DEFAULT_SITE_URL = "https://classnx.com";
const DEFAULT_TITLE = "ClassNX - School Management Software and School ERP";
const DEFAULT_DESCRIPTION =
  "ClassNX is school management software and school ERP for admissions, fees, attendance, exams, LMS, parent communication, and student information.";
const DEFAULT_KEYWORDS = [
  "school management software",
  "school management system",
  "school ERP software",
  "student information system",
  "school administration software",
  "student management software",
  "education ERP",
  "admission management system",
  "fee management software",
  "attendance management system",
  "exam management system",
  "timetable management software",
  "learning management system for schools",
  "parent communication app",
  "school analytics dashboard",
];

const normalizeKeywords = (keywords = DEFAULT_KEYWORDS) => {
  if (Array.isArray(keywords)) {
    return keywords.filter(Boolean).join(", ");
  }

  return keywords;
};

const getConfiguredSiteUrl = () => {
  const configuredUrl = process.env.REACT_APP_SITE_URL || DEFAULT_SITE_URL;

  try {
    return new URL(configuredUrl).origin;
  } catch {
    return window.location.origin;
  }
};

const upsertMeta = (selector, value) => {
  if (!value) return null;

  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    const nameMatch = selector.match(/\[name=['\"]([^'\"]+)['\"]\]/);
    const propertyMatch = selector.match(/\[property=['\"]([^'\"]+)['\"]\]/);

    if (nameMatch) tag.setAttribute("name", nameMatch[1]);
    if (propertyMatch) tag.setAttribute("property", propertyMatch[1]);

    document.head.appendChild(tag);
  }

  const previousValue = tag.getAttribute("content");
  tag.setAttribute("content", value);

  return () => {
    if (previousValue === null) {
      tag.remove();
      return;
    }
    tag.setAttribute("content", previousValue);
  };
};

const Seo = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  canonical = "/",
  image = "/logo512.png",
  keywords = DEFAULT_KEYWORDS,
  robots = "index, follow",
  type = "website",
}) => {
  const keywordContent = normalizeKeywords(keywords);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    const siteUrl = getConfiguredSiteUrl();
    const canonicalUrl = new URL(canonical, siteUrl).toString();
    const imageUrl = new URL(image, siteUrl).toString();

    const structuredData = [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "ClassNX",
        url: siteUrl,
        logo: imageUrl,
        description,
        keywords: keywordContent,
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "ClassNX",
        url: siteUrl,
        description,
        keywords: keywordContent,
      },
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "ClassNX",
        applicationCategory: "EducationalApplication",
        operatingSystem: "Web",
        url: canonicalUrl,
        description,
        keywords: keywordContent,
      },
    ];

    const previousJsonLd = document.head.querySelector('script[data-seo="json-ld"]');
    const previousJsonLdContent = previousJsonLd?.textContent || "";
    let jsonLdScript = previousJsonLd;
    if (!jsonLdScript) {
      jsonLdScript = document.createElement("script");
      jsonLdScript.type = "application/ld+json";
      jsonLdScript.setAttribute("data-seo", "json-ld");
      document.head.appendChild(jsonLdScript);
    }
    jsonLdScript.textContent = JSON.stringify(structuredData);

    const cleanupFns = [
      upsertMeta('meta[name="description"]', description),
      upsertMeta('meta[name="keywords"]', keywordContent),
      upsertMeta('meta[name="robots"]', robots),
      upsertMeta('meta[property="og:title"]', title),
      upsertMeta('meta[property="og:description"]', description),
      upsertMeta('meta[property="og:type"]', type),
      upsertMeta('meta[property="og:url"]', canonicalUrl),
      upsertMeta('meta[property="og:image"]', imageUrl),
      upsertMeta('meta[name="twitter:title"]', title),
      upsertMeta('meta[name="twitter:description"]', description),
      upsertMeta('meta[name="twitter:image"]', imageUrl),
    ].filter(Boolean);

    let canonicalLink = document.head.querySelector('link[rel="canonical"]');
    const previousCanonical = canonicalLink?.getAttribute("href");
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute("href", canonicalUrl);

    return () => {
      document.title = previousTitle;
      cleanupFns.forEach((cleanup) => cleanup?.());

      if (previousJsonLd) {
        previousJsonLd.textContent = previousJsonLdContent;
      } else {
        jsonLdScript?.remove();
      }

      if (previousCanonical === null || previousCanonical === undefined) {
        canonicalLink?.remove();
      } else if (canonicalLink) {
        canonicalLink.setAttribute("href", previousCanonical);
      }
    };
  }, [canonical, description, image, keywordContent, robots, title, type]);

  return null;
};

export default Seo;
